<template>
  <div class="data-table">
    <DataTableTitle
      v-model:visible-headers="visibleHeaders"
      class="table-title"
      :headers="headers"
      @weather="updateWeather($event)"
      @buffer="updateBuffer($event)"
      @days="updateDays($event)"
      @reload="reload" />
    <div ref="tableContainer" class="table-container">
      <ZDataTable
        v-if="tableReady"
        ref="table"
        item-key="id"
        :items="incidents"
        :search="search"
        :row-height="52"
        :headers="headers"
        :visible-headers="visibleHeaders"
        :row-class="rowClass"
        loading-style="skeleton"
        no-value-text="No Data">
        <template v-for="(calc, i) of calculationHeaders" :key="calc.value" #[`item.display.${calc.value}`]="{ item }">
          <!--          <v-chip :color="item.ratings[i]?.[buffer]?.calculationType?.color" class="calc-chip">-->
          <div
            v-if="item.ratings.length && item.ratings[i]?.[buffer]"
            class="calc-chip"
            :style="`background-color: ${item.ratings[i]?.[buffer]?.calculationType?.color}`">
            <p>{{ Math.trunc(item.ratings[i]?.[buffer]?.Max) }}</p>
          </div>
          <v-icon v-else>mdi-circle-off-outline</v-icon>
          <!--          </v-chip>-->
          <v-tooltip v-if="item.ratings.length && item.ratings[i]?.[buffer]" activator="parent" location="bottom" :offset="[0, -10]">
            {{ item.ratings[i]?.[buffer]?.calculationType?.displayName }}
          </v-tooltip>
        </template>
        <template #item.display.status="{ item }">
          <CalculationStatusChip v-if="item.status" class="status-chip" :status="item.status" />
        </template>
      </ZDataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
// Settings store import
import SettingsStore from '@/stores/settingsStore.ts'
const settings = SettingsStore()

// The rest
import { computed, onMounted, ref, watch } from 'vue'

import { storeToRefs } from 'pinia'

import { debounce } from 'lodash'
import { useI18n } from 'vue-i18n'

import { convertLatLonToDDM } from '@tsyl/radon'
import { ZDataTable } from '@tsyl/zapan2'
import type { Header, ItemListOptions } from '@tsyl/zapan2/lib/types/ZDataTable'

import CalculationStatusChip from '@/components/Dashboard/CalculationStatusChip.vue'
import DataTableTitle from '@/components/Dashboard/DataTableTitle.vue'
import { RIGHT_PANNEL_WIDTH } from '@/constants/layout'
import { getIncidents } from '@/services/api/incidents.ts'
import AuthStore from '@/stores/auth.ts'
import FiltersStore from '@/stores/FiltersStore.ts'
import type { CalculationType, Incident, Measurements } from '@/types/Incident.ts'
import { isCalculationFailed } from '@/utils/calculationStatus.ts'
import { dayKey, getForecastDays } from '@/utils/timeTools.ts'

const { t } = useI18n()
const authStore = AuthStore()
const { filtersOpen, filters, searchText } = storeToRefs(FiltersStore())
// ZDataTable is generic and exposes doReload through expose(), which ComponentInstance does not pick up
const table = ref<{ doReload: () => Promise<void> }>()
const tableContainer = ref<HTMLElement>()
const search = ref('')
const visibleHeaders = ref<string[]>()
const weather = ref('ERC')
const buffer = ref('valueBuffer1')
const days = ref(7)
const calculationHeaders = ref<Header[]>([])
const tableReady = ref(false)

onMounted(() => {
  let lastHeight = 0
  let observer: ResizeObserver | null = new ResizeObserver(() => {
    const height = tableContainer.value?.clientHeight ?? 0
    if (height > 0 && height === lastHeight) {
      tableReady.value = true
      observer?.disconnect()
      observer = null
    }
    lastHeight = height
  })
  if (tableContainer.value) observer.observe(tableContainer.value)
})

function buildCalculationHeaders() {
  const dates = getForecastDays(days.value)
  calculationHeaders.value = dates.map((date, index) => ({
    text: settings.formatForecastDay(date),
    value: `calculations${index}`,
    cellClass: 'no-background',
    transform: (v) => {
      if (v?.ratings[index] && v?.ratings[index].valueBuffer1) {
        return v.ratings[index].valueBuffer1.max?.toFixed(0)
      }
      return null
    },
    width: 100
  }))
}

buildCalculationHeaders()

watch(
  () => [days.value, settings.tenantDateFormat],
  () => buildCalculationHeaders()
)

async function incidents(options: ItemListOptions) {
  await authStore.waitUntilInitialised()
  const items = await getIncidents({
    limit: options.itemsPerPage,
    offset: options.itemsPerPage * (options.page - 1),
    sortBy: options.sortBy,
    desc: !options.sortDesc,
    search: search.value,
    filters: filters.value,
    includeCalculations: true
  })

  items.elements = items.elements.map((m: Incident) => {
    m.ratings = dateRangeCalculations(m.calculations)
    return m
  })

  return { items: items.elements, total: items.total }
}

const commonHeaders = computed(
  () =>
    [
      {
        text: t('views.dashboard.headers.name'),
        value: 'name',
        sortable: true
      },
      {
        text: t('views.dashboard.headers.incidentId'),
        value: 'incident_number'
      },
      {
        text: t('views.dashboard.headers.discovery'),
        value: 'declared_timestamp',
        transform: (v: string) => settings.formatStringDateTime(v),
        sortable: true
      },
      {
        text: t('views.dashboard.headers.size'),
        value: 'measurements',
        transform: (v) => v?.find((f: Measurements) => f?.measurementType?.name === 'Size')?.value,
        width: 150
      },
      {
        text: t('views.dashboard.headers.location'),
        value: 'location',
        transform: (v) => {
          const { latDDM, lonDDM } = convertLatLonToDDM(v?.geometry?.coordinates[1] as number, v?.geometry?.coordinates[0] as number)
          return [latDDM, lonDDM]
        }
      },
      {
        text: t('views.dashboard.headers.state'),
        value: 'state'
      },
      {
        text: t('views.dashboard.headers.jurisdiction'),
        value: 'jurisdictionalUnit'
      },
      {
        text: t('views.dashboard.headers.status'),
        value: 'status',
        width: 250
      },
      {
        text: t('views.dashboard.headers.calculationDate'),
        value: 'calculations',
        transform: (v: CalculationType[]) => settings.formatStringDateTime(v[0]?.createdAt)
      }
    ] as Header[]
)

const headers = computed(() => {
  return [...commonHeaders.value, ...calculationHeaders.value]
})

const rightBarWidth = computed(() => {
  return `${RIGHT_PANNEL_WIDTH}px`
})
const filterBarWidth = computed(() => {
  return filtersOpen.value ? `calc(100vw - ${rightBarWidth.value})` : '100vw'
})

function updateWeather(e: string) {
  weather.value = e
  reload()
}

function updateBuffer(e: string) {
  buffer.value = e
}

function updateDays(e: number) {
  days.value = Number(e)
}

function reload() {
  if (table.value) debouncedReload()
}

const debouncedReload = debounce(() => {
  if (table.value) table.value.doReload()
}, 100)

function dateRangeCalculations(data: CalculationType[]) {
  if (!data || !data?.length) return []
  const sortedData = data
    .filter((f) => f.calculationCategory?.name === weather.value)
    .sort((a, b) => new Date(a.forecastTimestamp).getTime() - new Date(b.forecastTimestamp).getTime())
  const dateArray = getForecastDays(days.value).map(dayKey)

  return dateArray
    .map((dateStr) => {
      const matchingCalc = sortedData.find((calc) => dateStr === dayKey(calc.forecastTimestamp))
      return matchingCalc || null
    })
    .filter((item): item is CalculationType => item !== null)
}

function rowClass(item: Incident) {
  const classes = []
  if (isCalculationFailed(item.status)) {
    classes.push('error-row')
  }
  // Not applicable at the moment
  // if (item.status === "completed") {
  //   classes.push("completed-row")
  // }
  return classes.join(' ')
}

watch(
  () => searchText.value,
  debounce((val) => {
    search.value = val
  }, 1000)
)

watch(
  () => [filters.value],
  () => {
    table.value?.doReload()
  }
)
</script>

<style lang="scss">
.error-row {
  background: rgba(var(--v-theme-error), 0.05) !important;
}
.completed-row {
  background: rgba(1, 255, 33, 0.1) !important;
}
.no-background {
  background: rgb(var(--v-theme-surface));
}
</style>

<style scoped lang="scss">
.data-table {
  --right-panel-width: v-bind(rightBarWidth);
  --filters-open-width: v-bind(filterBarWidth);
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--v-layout-top) - var(--v-layout-bottom));
  width: var(--filters-open-width);
}
.table-title {
  flex: 0 0 auto;
}
.table-container {
  flex: 1 1 auto;
  min-height: 0;
}
:deep(.select-items-per-page-wrapper) {
  width: 180px !important;
}
.calc-chip {
  font-size: 13px;
  font-weight: bold;
  color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  height: 3em;
  border-radius: 10px;
  filter: opacity(0.5);
}
.status-chip {
  border-radius: 10px;
}
.custom-tooltip {
  background-color: red !important;
  color: black !important;
  opacity: 1 !important;
}
</style>
